import { useQuery } from '@tanstack/react-query';
import { BadgeCheck, LoaderCircle, ShieldAlert, ShieldX } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';

interface PublicVerification {
  status: 'PENDING' | 'VALID' | 'REVOKED';
  titleAr: string;
  documentId: string;
  documentHashPrefix: string;
  version: string;
  issuedAt: string | null;
  revokedAt: string | null;
}

export function PublicVerificationPage() {
  const { token } = useParams();
  const query = useQuery({
    queryKey: ['public-verification', token],
    queryFn: () => api<PublicVerification>(`/api/public/verification/${token}`),
    enabled: Boolean(token),
    retry: false,
  });

  const valid = query.data?.status === 'VALID';
  return (
    <main className="pattern grid min-h-screen place-items-center px-4 py-10">
      <section className="glass w-full max-w-lg rounded-[2rem] p-6 text-center shadow-panel sm:p-9">
        <div className={`mx-auto grid h-20 w-20 place-items-center rounded-3xl text-white ${
          query.isLoading ? 'bg-ink' : valid ? 'bg-emerald-600' : 'bg-red-600'
        }`}>
          {query.isLoading ? (
            <LoaderCircle className="h-8 w-8 animate-spin" />
          ) : valid ? (
            <BadgeCheck className="h-9 w-9" />
          ) : query.isError ? (
            <ShieldAlert className="h-9 w-9" />
          ) : (
            <ShieldX className="h-9 w-9" />
          )}
        </div>
        <h1 className="mt-6 text-2xl font-black text-ink">
          {query.isLoading
            ? 'جارٍ التحقق…'
            : query.isError
              ? 'سجل التحقق غير موجود'
              : valid
                ? 'المستند موثّق'
                : 'سجل التحقق ملغى'}
        </h1>
        {query.data && (
          <div className="mt-6 space-y-3 rounded-2xl border border-ink/10 bg-white/70 p-5 text-start">
            {[
              ['المستند', query.data.titleAr],
              ['المعرف', query.data.documentId],
              ['الإصدار', query.data.version],
              ['بصمة SHA-256', query.data.documentHashPrefix],
              ['تاريخ الإصدار', query.data.issuedAt ? new Intl.DateTimeFormat('ar-SA', { dateStyle: 'long', timeStyle: 'short' }).format(new Date(query.data.issuedAt)) : '—'],
            ].map(([label, value]) => (
              <div key={label} className="flex items-start justify-between gap-4 border-b border-ink/5 pb-3 last:border-0 last:pb-0">
                <span className="text-[10px] font-bold text-slate-400">{label}</span>
                <span className={`text-end text-xs font-black ${label === 'بصمة SHA-256' ? 'font-mono' : ''}`} dir={label === 'بصمة SHA-256' ? 'ltr' : 'rtl'}>{value}</span>
              </div>
            ))}
          </div>
        )}
        <Link to="/" className="mt-6 inline-block text-xs font-black text-teal hover:underline">Details AI Platform</Link>
      </section>
    </main>
  );
}
